import { FC, ReactNode, useReducer, useState } from 'react';
import { useTranslation } from 'react-i18next';

import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import { Stack } from '@mui/system';

import ModalNotification, {
  ModalNotificationProps,
} from '@/shared/components/dialogs/ModalNotification';
import Provider from '@/shared/types/fulfilment/provider.type';
import ProviderAmazonFBA from '@/shared/types/fulfilment/providerAmazonFBA.type';
import ProviderKey from '@/shared/types/fulfilment/providerKey.enum';
import SupportedProvider from '@/shared/types/fulfilment/supportedProvider.type';

import AmazonFBADetailsForm from './components/AmazonFBADetailsForm';
import FulfilmentProviderDetailsForm from './components/FulfilmentProviderDetailsForm';
import { createInitialState, reducer } from './reducers/formReducer';
import FulfilmentProviderDetails from './types/fulfilmentProviderDetails.type';

type FormToShow = Partial<ProviderKey> | 'default';

type AddFulfilmentProviderFormDialogProps = {
  supportedProviders: SupportedProvider[];
  initialProvider: Provider | null;
  onClose: () => void;
  getFormValues: (
    values: FulfilmentProviderDetails & ProviderAmazonFBA,
    providerId?: string,
  ) => void;
};

const AddFulfilmentProviderFormDialog: FC<
  AddFulfilmentProviderFormDialogProps
> = ({ supportedProviders, initialProvider, onClose, getFormValues }) => {
  const { t } = useTranslation(['fulfilmentProviders', 'common']);

  const [activeForm, setActiveForm] = useState<FormToShow>('default');
  const [formState, dispatchForm] = useReducer(
    reducer,
    null,
    createInitialState(initialProvider),
  );

  const isFulfilmentProviderDetailsFormValid = () => {
    const errors: Partial<FulfilmentProviderDetails> = {};

    if (!formState.fulfilmentProviderDetails.fulfilment_provider_key) {
      errors.fulfilment_provider_key = t(
        'fulfilmentProviders:fulfilmentProviderKeyRequired',
      );
    }

    if (!formState.fulfilmentProviderDetails.provider_name) {
      errors.provider_name = t('fulfilmentProviders:providerNameRequired');
    }

    if (!formState.fulfilmentProviderDetails.provider_description) {
      errors.provider_description = t(
        'fulfilmentProviders:providerDescriptionRequired',
      );
    }

    dispatchForm({
      type: 'SET_FULFILMENT_PROVIDER_DETAILS_ERRORS',
      payload: { ...formState.fulfilmentProviderDetailsErrors, ...errors },
    });
    return !Object.values(errors).some((error) => !!error);
  };

  const isProviderFBADetailsFormValid = () => {
    const errors: Partial<ProviderAmazonFBA> = {};

    if (!formState.providerFBADetails.lwa_client_secret) {
      errors.lwa_client_secret = t(
        'fulfilmentProviders:lwaClientSecretRequired',
      );
    }

    if (!formState.providerFBADetails.lwa_app_id) {
      errors.lwa_app_id = t('fulfilmentProviders:lwaAppIdRequired');
    }

    if (!formState.providerFBADetails.refresh_token) {
      errors.refresh_token = t('fulfilmentProviders:refreshTokenRequired');
    }

    if (!formState.providerFBADetails.market_place_id) {
      errors.market_place_id = t('fulfilmentProviders:marketPlaceIdRequired');
    }

    dispatchForm({
      type: 'SET_PROVIDER_FBA_DETAILS_ERRORS',
      payload: { ...formState.providerFBADetailsErrors, ...errors },
    });
    return !Object.values(errors).some((error) => !!error);
  };

  const currentDisplayedForm: {
    [key in FormToShow]?: {
      title: string;
      component: ReactNode;
      actions: ModalNotificationProps['dialogProps']['actions'];
    };
  } = {
    default: {
      title: t('fulfilmentProviders:chooseFulfilmentProvider'),
      component: (
        <FulfilmentProviderDetailsForm
          supportedProviders={supportedProviders}
          formState={formState}
          dispatchForm={dispatchForm}
        />
      ),
      actions: [
        {
          label: t('common:next'),
          variant: 'contained',
          fullWidth: true,
          onClick: () => {
            const isFormValidResult = isFulfilmentProviderDetailsFormValid();

            if (isFormValidResult) {
              setActiveForm(
                formState.fulfilmentProviderDetails
                  .fulfilment_provider_key as ProviderKey,
              );
            }
          },
        },
      ],
    },
    [ProviderKey.FBA]: {
      title: t('fulfilmentProviders:fbaProviderDetails'),
      component: (
        <AmazonFBADetailsForm
          formState={formState}
          dispatchForm={dispatchForm}
        />
      ),
      actions: [
        {
          label: t('common:back'),
          variant: 'contained',
          icon: <KeyboardArrowLeftIcon sx={{ width: 20, height: 20 }} />,
          onClick: () => {
            setActiveForm('default');
          },
          sx: { gap: 1 },
        },
        {
          label: initialProvider
            ? t('fulfilmentProviders:updateProvider')
            : t('fulfilmentProviders:addProvider'),
          variant: 'contained',
          fullWidth: true,
          onClick: () => {
            const isFormValidResult = isProviderFBADetailsFormValid();

            if (isFormValidResult) {
              getFormValues(
                {
                  ...formState.fulfilmentProviderDetails,
                  ...formState.providerFBADetails,
                },
                initialProvider?.fulfilment_provider_id,
              );
            }
          },
        },
      ],
    },
  };

  return (
    <ModalNotification
      size='sm'
      fullWidth
      open
      onClose={onClose}
      dialogProps={{
        title: currentDisplayedForm[activeForm]?.title,
        content: (
          <Stack sx={{ py: 2 }}>
            {currentDisplayedForm[activeForm]?.component}
          </Stack>
        ),
        actions: currentDisplayedForm[activeForm]?.actions || [],
      }}
    />
  );
};

export default AddFulfilmentProviderFormDialog;
